// Lion Auto listing table's sort and Auto rank: the sort is a list of
// { column, direction } (first = strongest; [] = the default order), a header
// click turns it into the next one (Shift adds a column), Smart sort is one
// such list (qty bands: singles, then 2s and 3s together, then 4+ together;
// rows worst to best inside each band), and Auto rank reads the ladder order
// off the sorted rows. Rows
// are the table's ({ key, own, l }). Pure; tested in lionAutoSort.test.js.
import { compareRows, rowText } from './lionAutoRows';
import { groupSortKey } from './lionAutoGroups';
import { MAX_LADDER, cleanLadderIds } from './lionAutoLadder';

/**
 * Smart sort: qty bands smallest first (qty 1, then qty 2-3 together, then
 * qty 4+ together; the Qty entry is `banded`), rows worst to best, back rows
 * first, inside each band (Row descending, so rank 1 lands on the worst row)
 */
export const SMART_SORT = Object.freeze([
  Object.freeze({ column: 'qty', direction: 'asc', banded: true }),
  Object.freeze({ column: 'row', direction: 'desc' }),
]);

// Smart sort's qty bands, in order: singles (a missing or 0 qty too), 2-3,
// then 4 and up
const QTY_BAND_SINGLES = 1;
const QTY_BAND_TWO_THREE = 2;
const QTY_BAND_FOUR_UP = 3;
const SINGLES_MAX_QTY = 1;
const TWO_THREE_MAX_QTY = 3;
function qtyBand(q) {
  if (q <= SINGLES_MAX_QTY) return QTY_BAND_SINGLES;
  if (q <= TWO_THREE_MAX_QTY) return QTY_BAND_TWO_THREE;
  return QTY_BAND_FOUR_UP;
}

// Default order: priced rows by L ascending, unpriced ones (not listed on
// Vivid) last; ties put the bigger quantity first, then a stable key so rows
// don't jump between refetches
const priceOf = (r) => {
  const n = Number(r.l.unitPrice);
  return Number.isFinite(n) && n > 0 ? n : null;
};
function defaultOrder(a, b) {
  const pa = priceOf(a);
  const pb = priceOf(b);
  if ((pa == null) !== (pb == null)) return pa == null ? 1 : -1;
  if (pa != null && pa !== pb) return pa - pb;
  const qa = Number(a.l.quantity) || 0;
  const qb = Number(b.l.quantity) || 0;
  if (qa !== qb) return qb - qa;
  if (a.key === b.key) return 0;
  return a.key < b.key ? -1 : 1;
}

// A sorted header as on Compare (sortListings): text naturally ("2" before
// "10"), numbers and dates numerically with a missing value as 0. Row reads
// as the Rows filter does (compareRows: 2, 10, A, B, AA, GA2, GA10); no row
// label is a missing value.
const timeOf = (v) => (v ? new Date(v).getTime() : 0);
const num = (v) => Number(v) || 0;
const SORT_VALUES = {
  section: (l) => (l.displayName || l.section || '').toLowerCase(),
  row: (l) => rowText(l.row) || null,
  seats: (l) => num(l.lowSeat),
  qty: (l) => num(l.quantity),
  cost: (l) => num(l.unitCostAverage),
  bprice: (l) => num(l.listPrice),
  lprice: (l) => num(l.unitPrice),
  updated: (l) => timeOf(l.lastPriceUpdate),
  purchased: (l) => timeOf(l.createdDate),
};
// A banded Qty (Smart sort's) reads the qty band, not the quantity
const qtyBandOf = (l) => qtyBand(num(l.quantity));
const sortable = (column) => column === 'group' || Object.prototype.hasOwnProperty.call(SORT_VALUES, column);

// One column's comparator. Group sorts on the names of the groups taking the
// row (groupsOf(row) → names; groupSortKey). A listing in no group (every
// market row among them) goes after the rest on ascending; a row with no
// label goes last either way (an unknown row is neither worst nor best).
function columnOrder({ column, direction, banded }, groupsOf) {
  const valueOf = banded === true && column === 'qty' ? qtyBandOf : SORT_VALUES[column];
  const value = column === 'group'
    ? (r) => groupSortKey(groupsOf ? groupsOf(r) : undefined)
    : (r) => valueOf(r.l);
  const sign = direction === 'desc' ? -1 : 1;
  const missingSign = column === 'row' ? 1 : sign;
  return (a, b) => {
    const av = value(a);
    const bv = value(b);
    if (av === null || bv === null) return av === bv ? 0 : missingSign * (av === null ? 1 : -1);
    if (column === 'row') return sign * compareRows(av, bv);
    if (typeof av === 'string' && typeof bv === 'string') return sign * av.localeCompare(bv, undefined, { numeric: true });
    return sign * (av - bv);
  };
}

/**
 * The sort after a header click. A plain click on a column in the sort flips
 * its direction where it stands (so Row flips inside Smart sort; a flipped
 * Qty comes back plain, without bands, so Smart sort goes off); on any
 * other column it sorts on that column alone, ascending. A Shift-click
 * (additive) adds a column ascending at the end, flips it when it is in the
 * sort ascending, and takes it out when descending. Never changes the sort it
 * is given.
 * @param {{column: string, direction: 'asc'|'desc'}[]} sorts
 * @param {string} column
 * @param {boolean} [additive]  Shift was held
 * @returns {{column: string, direction: 'asc'|'desc'}[]}
 */
export function nextSort(sorts, column, additive = false) {
  const list = Array.isArray(sorts) ? sorts : [];
  const at = list.findIndex(s => s.column === column);
  if (at < 0) return additive ? [...list, { column, direction: 'asc' }] : [{ column, direction: 'asc' }];
  if (additive && list[at].direction === 'desc') return list.filter((_, i) => i !== at);
  const direction = list[at].direction === 'asc' ? 'desc' : 'asc';
  return list.map((s, i) => (i === at ? { column, direction } : s));
}

/**
 * Smart sort is on: banded Qty ascending first, Row second (the rows either
 * way, so flipping them with a click on Row keeps it on), nothing else. A
 * plain Qty-then-Row sort built by hand (no bands) is not Smart sort.
 */
export function isSmartSort(sorts) {
  return Array.isArray(sorts) && sorts.length === 2
    && sorts[0].column === 'qty' && sorts[0].direction === 'asc' && sorts[0].banded === true
    && sorts[1].column === 'row';
}

/**
 * The table's comparator: each column of the sort in turn (columns the table
 * can't sort on are skipped), ties then broken by the default order, so equal
 * rows never jump between refetches.
 * @param {{column: string, direction: 'asc'|'desc', banded?: boolean}[]} sorts  [] = the default order; banded Qty compares qty bands
 * @param {{groupsOf?: Function}} [options]  groupsOf(row) → names of the groups taking it (Group column)
 * @returns {(a: object, b: object) => number}
 */
export function sortComparator(sorts, { groupsOf } = {}) {
  const orders = (Array.isArray(sorts) ? sorts : [])
    .filter(s => s && sortable(s.column))
    .map(s => columnOrder(s, groupsOf));
  if (orders.length === 0) return defaultOrder;
  return (a, b) => {
    for (const order of orders) {
      const d = order(a, b);
      if (d !== 0) return d;
    }
    return defaultOrder(a, b);
  };
}

/**
 * Auto rank: the ladder order the table shows. Our rows only, in the order
 * given, that are group members now (memberIds: id strings; null while the
 * group's result is not in, then none) and not excluded by hand; ids as
 * numbers, each once, at most `max` (the server's 500).
 * @param {object[]} rows  the table's sorted rows ({ own, l })
 * @param {{memberIds: Set<string>|null, isExcluded?: (id) => boolean, max?: number}} [options]
 * @returns {number[]}
 */
export function autoRankIds(rows, { memberIds = null, isExcluded = () => false, max = MAX_LADDER } = {}) {
  if (!memberIds || !Array.isArray(rows)) return [];
  const ids = [];
  for (const r of rows) {
    if (!r?.own || !r.l) continue;
    const id = r.l.id;
    if (!memberIds.has(String(id)) || isExcluded(id)) continue;
    ids.push(id);
  }
  return cleanLadderIds(ids).slice(0, max);
}
